import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader } from '@mantine/core';
import { IoChevronBack, IoChevronForward, IoChatbubblesOutline, IoClose, IoImageOutline, IoSend } from 'react-icons/io5';
import { Breadcrumbs } from '@components';
import { Button, Textarea } from '@shared/ui';
import {
  clientOrderChatApi,
  CLIENT_ORDER_CHAT_MAX_IMAGES,
} from '@modules/client-order-chat/api/client-order-chat.api';
import {
  appendOrderChatMessageToCache,
  mergeOrderChatMessages,
  ORDER_CHAT_SESSIONS_QUERY_KEY,
  ORDER_CHAT_UNREAD_QUERY_KEY,
  orderChatMessagesQueryKey,
} from '@modules/client-order-chat/chat-cache';
import { useClientOrderChatSocket } from '@modules/client-order-chat/hooks/useClientOrderChatSocket';
import type {
  ClientOrderChatMessage,
  ClientOrderChatMessagesResponse,
  ClientOrderChatSession,
} from '@modules/client-order-chat/types';
import { isSystemOrderChatMessage } from '@modules/client-order-chat/types';
import { ChatBubbleImages } from '@modules/client-order-chat/components/ChatBubbleImages';
import { devProxyS3Url } from '@shared/utils/dev-proxy-s3-url';
import '@shared/styles/page.css';
import './SellerOrderChatsPage.css';

function messageKey(message: ClientOrderChatMessage) {
  return `${message.id}:${message.createdAt}`;
}

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function chatTime(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function threadListStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  const sameYear = d.getFullYear() === now.getFullYear();
  return sameYear
    ? `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}`
    : `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
}

function userInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return `${parts[0].slice(0, 1)}${parts[1].slice(0, 1)}`.toUpperCase();
}

function sessionPreview(session: ClientOrderChatSession): string {
  const body = session.lastMessage?.body?.trim();
  if (body) return body;
  const images = session.lastMessage?.imageUrls?.length ?? 0;
  if (images === 1) return 'Фото';
  if (images > 1) return `Фото (${images})`;
  return 'Нет сообщений';
}

function sessionTitle(session: ClientOrderChatSession) {
  const order = session.orderNumber
    ? `Заказ №${session.orderNumber}`
    : session.orderId
      ? `Заказ #${session.orderId}`
      : 'Вопрос по заказу';
  const client = session.userName?.trim() || `Клиент #${session.userId}`;
  return `${order} · ${client}`;
}

type PendingImage = {
  id: string;
  file: File;
  previewUrl: string;
};

export default function SellerOrderChatsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const problemIdFromQuery = Number(searchParams.get('problemId'));
  const selectedProblemId =
    Number.isFinite(problemIdFromQuery) && problemIdFromQuery > 0
      ? problemIdFromQuery
      : null;
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const messagesContentRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);
  const ignoreScrollUntilRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const sessionsQuery = useQuery({
    queryKey: ORDER_CHAT_SESSIONS_QUERY_KEY,
    queryFn: () => clientOrderChatApi.listSessions(),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const sessions = sessionsQuery.data?.items ?? [];
  const activeSession =
    sessions.find((s) => s.problemMessageId === selectedProblemId) ?? null;

  const openChat = useCallback(
    (problemMessageId: number) => {
      setSearchParams({ problemId: String(problemMessageId) }, { replace: true });
    },
    [setSearchParams],
  );

  const closeChat = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // Сброс черновика при закрытии / смене чата
  const prevSelectedRef = useRef(selectedProblemId);
  useEffect(() => {
    if (prevSelectedRef.current === selectedProblemId) return;
    prevSelectedRef.current = selectedProblemId;
    setDraft('');
    setPendingImages((prev) => {
      prev.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      return [];
    });
  }, [selectedProblemId]);

  const messagesQueryKey = orderChatMessagesQueryKey(selectedProblemId ?? 0);

  const messagesQuery = useQuery({
    queryKey: messagesQueryKey,
    queryFn: async () => {
      const data = await clientOrderChatApi.getMessages(selectedProblemId!, {
        limit: 100,
      });
      const prev = queryClient.getQueryData<ClientOrderChatMessagesResponse>(
        messagesQueryKey,
      );
      return mergeOrderChatMessages(prev, data);
    },
    enabled: selectedProblemId != null,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const markReadMutation = useMutation({
    mutationFn: (problemMessageId: number) =>
      clientOrderChatApi.markRead(problemMessageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ORDER_CHAT_UNREAD_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ORDER_CHAT_SESSIONS_QUERY_KEY });
    },
  });

  const clearPendingImages = useCallback(() => {
    setPendingImages((prev) => {
      prev.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      return [];
    });
  }, []);

  const sendMutation = useMutation({
    mutationFn: ({ body, images }: { body: string; images: File[] }) =>
      clientOrderChatApi.sendMessage(selectedProblemId!, body, images),
    onSuccess: (message) => {
      setDraft('');
      clearPendingImages();
      stickToBottomRef.current = true;
      if (selectedProblemId != null) {
        appendOrderChatMessageToCache(queryClient, selectedProblemId, message);
      }
      queryClient.invalidateQueries({ queryKey: ORDER_CHAT_SESSIONS_QUERY_KEY });
    },
  });

  useClientOrderChatSocket({
    onMessage: ({ message, problemMessageId }) => {
      const pid =
        problemMessageId ??
        message.relatedMessageId ??
        (message.systemEvent === 'ORDER_PROBLEM' ? message.id : null);
      if (pid == null) {
        // Fallback: без problemMessageId всё равно подтянем ленту активного чата
        queryClient.invalidateQueries({ queryKey: ORDER_CHAT_SESSIONS_QUERY_KEY });
        if (selectedProblemId != null) {
          queryClient.invalidateQueries({
            queryKey: orderChatMessagesQueryKey(selectedProblemId),
          });
        }
        return;
      }
      appendOrderChatMessageToCache(queryClient, pid, message, {
        isActive: pid === selectedProblemId,
      });
      queryClient.invalidateQueries({ queryKey: ORDER_CHAT_SESSIONS_QUERY_KEY });
      if (pid === selectedProblemId) {
        markReadMutation.mutate(pid);
      }
    },
    onUnread: ({ scope, count }) => {
      if (scope !== 'seller') return;
      queryClient.setQueryData(ORDER_CHAT_UNREAD_QUERY_KEY, { count });
    },
  });

  useEffect(() => {
    if (selectedProblemId == null || !messagesQuery.isSuccess) return;
    markReadMutation.mutate(selectedProblemId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProblemId, messagesQuery.isSuccess]);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  const messagesSignature =
    messagesQuery.data?.items?.map((m) => m.id).join(',') ?? '';

  // Синхронно при смене чата — до useLayoutEffect, иначе stick ещё false с прошлого чата
  const prevProblemIdRef = useRef<number | null>(null);
  if (prevProblemIdRef.current !== selectedProblemId) {
    prevProblemIdRef.current = selectedProblemId;
    stickToBottomRef.current = true;
    ignoreScrollUntilRef.current = Date.now() + 900;
  }

  useLayoutEffect(() => {
    if (!stickToBottomRef.current) return;
    scrollToBottom();
    const id = requestAnimationFrame(() => {
      scrollToBottom();
      requestAnimationFrame(scrollToBottom);
    });
    return () => cancelAnimationFrame(id);
  }, [
    messagesSignature,
    messagesQuery.isSuccess,
    messagesQuery.isFetching,
    selectedProblemId,
    sendMutation.isSuccess,
    pendingImages.length,
    scrollToBottom,
  ]);

  useEffect(() => {
    if (!stickToBottomRef.current) return undefined;
    // Добиваем низ после подгрузки картинок (layout сетки асинхронный)
    const timers = [50, 150, 350, 700].map((ms) =>
      window.setTimeout(() => {
        if (stickToBottomRef.current) scrollToBottom();
      }, ms),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [selectedProblemId, messagesSignature, messagesQuery.isSuccess, scrollToBottom]);

  useEffect(() => {
    const content = messagesContentRef.current;
    if (!content) return undefined;
    const ro = new ResizeObserver(() => {
      if (stickToBottomRef.current) scrollToBottom();
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, [selectedProblemId, messagesQuery.isSuccess, messagesSignature, scrollToBottom]);

  const handleMessagesScroll = () => {
    if (Date.now() < ignoreScrollUntilRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distance < 120;
  };
  const handlePickImages = (files: FileList | null) => {
    if (!files?.length) return;
    const room = CLIENT_ORDER_CHAT_MAX_IMAGES - pendingImages.length;
    const next: PendingImage[] = [];
    Array.from(files)
      .filter((file) => file.type.startsWith('image/'))
      .slice(0, Math.max(room, 0))
      .forEach((file) => {
        next.push({
          id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
          file,
          previewUrl: URL.createObjectURL(file),
        });
      });
    if (next.length === 0) return;
    setPendingImages((prev) =>
      [...prev, ...next].slice(0, CLIENT_ORDER_CHAT_MAX_IMAGES),
    );
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingImage = (id: string) => {
    setPendingImages((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((item) => item.id !== id);
    });
  };

  const handleSend = () => {
    const body = draft.trim();
    const images = pendingImages.map((item) => item.file);
    if ((!body && images.length === 0) || sendMutation.isPending || !selectedProblemId) {
      return;
    }
    if (activeSession?.resolved) return;
    sendMutation.mutate({ body, images });
  };

  const canSend =
    Boolean(draft.trim() || pendingImages.length > 0) && !activeSession?.resolved;

  const sortedMessages = useMemo(
    () => [...(messagesQuery.data?.items ?? [])].sort((a, b) => a.id - b.id),
    [messagesQuery.data?.items],
  );

  const resolved =
    messagesQuery.data?.resolved ?? activeSession?.resolved ?? false;

  return (
    <div
      className={`page-container seller-order-chats-page${
        selectedProblemId != null ? ' seller-order-chats-page--chat-open' : ''
      }`}
    >
      <div className="seller-order-chats-page__intro">
        <Breadcrumbs
          items={[
            { label: 'Главная', path: '/' },
            { label: 'Чаты по заказам' },
          ]}
        />

        <div className="page-header seller-order-chats-page__page-header">
          <div className="page-header-top">
            <h1>Чаты по заказам</h1>
          </div>
          <p className="seller-order-chats-page__subtitle">
            Вопросы по обращениям клиентов, к которым вас подключила поддержка.
          </p>
        </div>
      </div>

      <div
        className={`content-wrapper seller-order-chats-page__layout${
          selectedProblemId != null
            ? ' seller-order-chats-page__layout--chat-open'
            : ''
        }`}
      >
        <aside className="seller-order-chats-page__list">
          {sessionsQuery.isLoading ? (
            <div className="seller-order-chats-page__state">
              <Loader size="sm" color="var(--accent)" />
            </div>
          ) : null}
          {!sessionsQuery.isLoading && sessions.length === 0 ? (
            <div className="seller-order-chats-page__empty-list">
              <IoChatbubblesOutline />
              <p>Пока нет чатов по заказам</p>
            </div>
          ) : null}
          {sessions.map((session) => {
            const active = session.problemMessageId === selectedProblemId;
            const clientName = session.userName?.trim() || `Клиент #${session.userId}`;
            const stampIso = session.lastMessage?.createdAt ?? session.updatedAt;
            return (
              <button
                key={session.problemMessageId}
                type="button"
                className={`seller-order-chats-page__session ${
                  active ? 'seller-order-chats-page__session--active' : ''
                }`}
                onClick={() => openChat(session.problemMessageId)}
              >
                <span className="seller-order-chats-page__session-avatar" aria-hidden>
                  {userInitials(clientName)}
                </span>
                <span className="seller-order-chats-page__session-body">
                  <span className="seller-order-chats-page__session-top">
                    <span className="seller-order-chats-page__session-title">
                      {session.orderNumber
                        ? `№${session.orderNumber}`
                        : `Заказ #${session.orderId ?? '—'}`}
                      <span className="seller-order-chats-page__session-sep">
                        ·
                      </span>
                      {clientName}
                    </span>
                    <span className="seller-order-chats-page__session-top-right">
                      {session.resolved ? (
                        <span className="seller-order-chats-page__session-status">
                          Закрыт
                        </span>
                      ) : null}
                      {stampIso ? (
                        <time className="seller-order-chats-page__session-stamp" dateTime={stampIso}>
                          {threadListStamp(stampIso)}
                        </time>
                      ) : null}
                    </span>
                  </span>
                  <span className="seller-order-chats-page__session-preview-row">
                    <span className="seller-order-chats-page__session-preview">
                      {sessionPreview(session)}
                    </span>
                    {session.unreadCount > 0 ? (
                      <span className="seller-order-chats-page__session-badge">
                        {session.unreadCount > 99 ? '99+' : session.unreadCount}
                      </span>
                    ) : null}
                  </span>
                </span>
              </button>
            );
          })}
        </aside>

        <div className="seller-order-chats-page__chat">
          {!selectedProblemId ? (
            <div className="seller-order-chats-page__empty seller-order-chats-page__empty--desktop">
              <p>Выберите диалог слева</p>
            </div>
          ) : (
            <>
              <div className="seller-order-chats-page__chat-toolbar">
                <button
                  type="button"
                  className="seller-order-chats-page__chat-back"
                  onClick={closeChat}
                  aria-label="К списку чатов"
                >
                  <IoChevronBack size={18} />
                </button>
                <button
                  type="button"
                  className={`seller-order-chats-page__chat-header${
                    activeSession?.orderId != null
                      ? ' seller-order-chats-page__chat-header--link'
                      : ''
                  }`}
                  disabled={activeSession?.orderId == null}
                  onClick={() => {
                    if (activeSession?.orderId == null) return;
                    navigate(`/orders/${activeSession.orderId}`, {
                      state: {
                        from: 'order-chats',
                        problemMessageId: activeSession.problemMessageId,
                      },
                    });
                  }}
                >
                  <span className="seller-order-chats-page__session-avatar" aria-hidden>
                    {userInitials(
                      activeSession?.userName?.trim() ||
                        (activeSession ? `Клиент #${activeSession.userId}` : 'Клиент'),
                    )}
                  </span>
                  <div className="seller-order-chats-page__chat-header-text">
                    <strong>
                      {activeSession
                        ? sessionTitle(activeSession)
                        : `Обращение #${selectedProblemId}`}
                    </strong>
                    {resolved ? (
                      <span className="seller-order-chats-page__resolved">
                        Закрыто
                      </span>
                    ) : null}
                  </div>
                  {activeSession?.orderId != null ? (
                    <IoChevronForward
                      className="seller-order-chats-page__chat-header-arrow"
                      aria-hidden
                    />
                  ) : null}
                </button>
              </div>

              <div
                ref={scrollRef}
                className="seller-order-chats-page__messages"
                onScroll={handleMessagesScroll}
              >
                <div
                  ref={messagesContentRef}
                  className="seller-order-chats-page__messages-inner"
                >
                  {messagesQuery.isLoading ? (
                    <div className="seller-order-chats-page__state">
                      <Loader size="sm" color="var(--accent)" />
                    </div>
                  ) : null}
                  {!messagesQuery.isLoading && sortedMessages.length === 0 ? (
                    <div className="seller-order-chats-page__empty">
                      <p>Сообщений пока нет</p>
                    </div>
                  ) : null}
                  {sortedMessages.map((message) => {
                    if (isSystemOrderChatMessage(message)) {
                      return (
                        <div
                          key={messageKey(message)}
                          className="seller-order-chats-page__system"
                        >
                          <span>{message.body}</span>
                        </div>
                      );
                    }
                    const mine = message.senderKind === 'SELLER';
                    const images = (message.imageUrls ?? [])
                      .map((url) => devProxyS3Url(url) || url)
                      .filter(Boolean);
                    const label = mine
                      ? 'Вы'
                      : message.senderKind === 'SUPER_ADMIN'
                        ? message.senderName || 'Поддержка'
                        : message.senderName || 'Клиент';
                    return (
                      <div
                        key={messageKey(message)}
                        className={`seller-order-chats-page__bubble ${mine
                            ? 'seller-order-chats-page__bubble--mine'
                            : 'seller-order-chats-page__bubble--theirs'
                          }${images.length > 0 ? ' seller-order-chats-page__bubble--has-images' : ''}`}
                      >
                        <div className="seller-order-chats-page__bubble-meta">
                          {label} · {chatTime(message.createdAt)}
                        </div>
                        {images.length > 0 ? <ChatBubbleImages urls={images} /> : null}
                        {message.body ? <p>{message.body}</p> : null}
                      </div>
                    );
                  })}
                  <div
                    className="seller-order-chats-page__messages-end"
                    aria-hidden
                  />
                </div>
              </div>

              <div className="seller-order-chats-page__composer">
                {resolved ? (
                  <p className="seller-order-chats-page__composer-closed">
                    Обращение закрыто — писать нельзя
                  </p>
                ) : (
                  <>
                    {pendingImages.length > 0 ? (
                      <div className="seller-order-chats-page__composer-previews">
                        {pendingImages.map((item) => (
                          <div
                            key={item.id}
                            className="seller-order-chats-page__composer-preview"
                          >
                            <img src={item.previewUrl} alt="" />
                            <button
                              type="button"
                              className="seller-order-chats-page__composer-preview-remove"
                              onClick={() => removePendingImage(item.id)}
                              aria-label="Убрать фото"
                            >
                              <IoClose />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    <div className="seller-order-chats-page__composer-row">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        hidden
                        onChange={(event) =>
                          handlePickImages(event.currentTarget.files)
                        }
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        className="seller-order-chats-page__composer-attach"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={
                          sendMutation.isPending ||
                          pendingImages.length >= CLIENT_ORDER_CHAT_MAX_IMAGES
                        }
                        aria-label="Прикрепить фото"
                      >
                        <IoImageOutline />
                      </Button>
                      <Textarea
                        className="seller-order-chats-page__composer-input"
                        placeholder="Напишите сообщение…"
                        minRows={1}
                        maxRows={5}
                        autosize
                        value={draft}
                        onChange={(e) => setDraft(e.currentTarget.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSend();
                          }
                        }}
                      />
                      <Button
                        className="seller-order-chats-page__composer-send"
                        onClick={handleSend}
                        loading={sendMutation.isPending}
                        disabled={!canSend}
                        aria-label="Отправить"
                      >
                        <IoSend />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
